import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../auth/AuthContext";
import Select from "../components/Select";
import ScenathonCharts from "../components/Scenathon/ScenathonCharts";
import Comparison from "./ComparisonScenathon";

const API = process.env.REACT_APP_API_URL;
const STORAGE_KEY = "scenathon_send_results_page_v5";

function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
}) {
  return (
    <Select
      label={label}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full"
    />
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
        {label}
      </div>
      <div className="mt-2 text-lg font-semibold text-[#111827]">
        {value || "—"}
      </div>
    </div>
  );
}

function normalizeResultsPayload(payload) {
  const root = Array.isArray(payload) ? payload[0] : payload;
  if (!root || typeof root !== "object") return null;

  const pathwayKey = Object.keys(root)[0];
  if (!pathwayKey) return null;

  const node = root[pathwayKey];
  const chartsBlock = node?.charts?.[0] || {};

  return {
    pathwayKey,
    node,
    charts: chartsBlock,
  };
}

function normalizeCountryOptionLabel(option) {
  if (typeof option === "string") {
    return option.trim().replace(/\s+/g, " ");
  }

  if (!option || typeof option !== "object") return "";

  const rawLabel = option.label || option.value || "";
  return String(rawLabel).trim().replace(/\s+/g, " ");
}

function extractCountriesFromCharts(chartsBlock) {
  if (!chartsBlock || typeof chartsBlock !== "object") return [];

  const collected = Object.values(chartsBlock).flatMap((section) => {
    if (!section || typeof section !== "object") return [];
    if (!Array.isArray(section.countries)) return [];

    return section.countries
      .map(normalizeCountryOptionLabel)
      .filter(Boolean);
  });

  const uniqueCountries = [...new Set(collected)];
  const nonGlobalCountries = uniqueCountries.filter(
    (country) => country.toLowerCase() !== "global",
  );

  return nonGlobalCountries.length > 0 ? nonGlobalCountries : uniqueCountries;
}

function readStoredState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeStoredState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export default function ScenathonSendResultsPage() {
  const { accessToken, csrfToken, logout, user, selectedScenathon } = useAuth();

  const stored = useMemo(() => readStoredState(), []);

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [loadingDefaultPathways, setLoadingDefaultPathways] = useState(false);

  const [scenathons, setScenathons] = useState([]);
  const [pathways, setPathways] = useState([]);
  const [dashboardPathways, setDashboardPathways] = useState([]);
  const [defaultPathways, setDefaultPathways] = useState([]);

  // envío
  const [sendScenathonId, setSendScenathonId] = useState(
    selectedScenathon?.id || stored?.sendScenathonId || "",
  );
  const [selectedPathwayId, setSelectedPathwayId] = useState(
    stored?.selectedPathwayId || "",
  );
  const [selectedDefaultPathwayId, setSelectedDefaultPathwayId] = useState(
    stored?.selectedDefaultPathwayId || "",
  );

  // resultados
  const [resultsScenathonId, setResultsScenathonId] = useState(
    stored?.resultsScenathonId || selectedScenathon?.id || "",
  );
  const [resultsPathwayId, setResultsPathwayId] = useState(
    stored?.resultsPathwayId || "",
  );
  const [sectionTab, setSectionTab] = useState(stored?.sectionTab || "dashboard");
  const [dashboardTab, setDashboardTab] = useState(stored?.dashboardTab || "results");

  const [resultsPayload, setResultsPayload] = useState(
    stored?.resultsPayload || null,
  );
  const [resultsError, setResultsError] = useState(stored?.resultsError || "");

  // 🌍 Países de trade_adjustment_submissions
  const [countriesFromSubmissions, setCountriesFromSubmissions] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  const canSeeSubmission =
    user?.type_id !== 2 && user?.type_id !== 5 && user?.type_id !== 4;

  const apiFetch = async (url, options = {}) => {
    const headers = {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
      ...(options.headers || {}),
    };

    const res = await fetch(url, {
      credentials: "include",
      ...options,
      headers,
    });

    if (res.status === 401) {
      logout?.();
      throw new Error("Session expired");
    }

    return res;
  };

  useEffect(() => {
    writeStoredState({
      sendScenathonId: selectedScenathon?.id || sendScenathonId,
      selectedPathwayId,
      selectedDefaultPathwayId,
      resultsScenathonId,
      resultsPathwayId,
      resultsPayload,
      resultsError,
      sectionTab,
      dashboardTab,
    });
  }, [
    sendScenathonId,
    selectedPathwayId,
    selectedDefaultPathwayId,
    resultsScenathonId,
    resultsPathwayId,
    resultsPayload,
    resultsError,
    sectionTab,
    dashboardTab,
  ]);

  useEffect(() => {
    if (!canSeeSubmission && sectionTab === "submission") {
      setSectionTab("dashboard");
    }
  }, [canSeeSubmission, sectionTab]);

  useEffect(() => {
    let alive = true;

    async function loadPageData() {
      setLoading(true);
      try {
        const [scRes, pwRes] = await Promise.all([
          apiFetch(`${API}/api/scenathon/scenathons`),
          apiFetch(`${API}/api/pathways/pathways`),
        ]);

        if (!scRes.ok) throw new Error(`Scenathons HTTP ${scRes.status}`);
        if (!pwRes.ok) throw new Error(`Pathways HTTP ${pwRes.status}`);

        const scData = await scRes.json();
        const pwData = await pwRes.json();

        if (!alive) return;

        setScenathons(Array.isArray(scData?.scenathons) ? scData.scenathons : []);
        setPathways(Array.isArray(pwData) ? pwData : []);
      } catch (err) {
        console.error(err);
        if (!alive) return;

        Swal.fire({
          icon: "error",
          title: "Could not load the page",
          text: err.message || "Error loading scenathons and pathways.",
        });
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadPageData();

    return () => {
      alive = false;
    };
  }, []);

  // Cargar default pathways del scenathon seleccionado para envío
  useEffect(() => {
    let alive = true;

    async function loadDefaultPathways() {
      if (!sendScenathonId) {
        setDefaultPathways([]);
        setSelectedDefaultPathwayId("");
        return;
      }

      setLoadingDefaultPathways(true);
      setSelectedDefaultPathwayId("");

      try {
        const res = await apiFetch(
          `${API}/api/scenathon/default-pathways/${sendScenathonId}`,
        );

        if (!res.ok) {
          const err = await res.json().catch(() => null);
          throw new Error(err?.error || `HTTP ${res.status}`);
        }

        const data = await res.json();

        if (!alive) return;
        setDefaultPathways(Array.isArray(data?.defaultPathways) ? data.defaultPathways : []);
      } catch (err) {
        console.error("Error loading default pathways:", err);
        if (!alive) return;
        setDefaultPathways([]);
        Swal.fire({
          icon: "error",
          title: "Could not load default pathways",
          text: err.message || "Error loading default pathways.",
        });
      } finally {
        if (alive) setLoadingDefaultPathways(false);
      }
    }

    loadDefaultPathways();

    return () => {
      alive = false;
    };
  }, [sendScenathonId]);

  useEffect(() => {
    setResultsPayload(null);
    setResultsError("");
  }, [resultsScenathonId, resultsPathwayId]);

  useEffect(() => {
    if (selectedScenathon?.id && selectedScenathon?.id !== sendScenathonId) {
      setSendScenathonId(selectedScenathon.id);
    }
  }, [selectedScenathon?.id, sendScenathonId]);

  useEffect(() => {
    if (selectedScenathon?.id && selectedScenathon?.id !== resultsScenathonId) {
      setResultsScenathonId(selectedScenathon.id);
    }
  }, [selectedScenathon?.id, resultsScenathonId]);

  useEffect(() => {
    if (!resultsScenathonId || !resultsPathwayId) return;
    loadScenathonResults(resultsScenathonId, resultsPathwayId);
  }, [resultsScenathonId, resultsPathwayId]);

  const loadScenathonResults = async (scenathonId, pathwayId) => {
    setResultsPayload(null);
    setResultsError("");

    if (!scenathonId || !pathwayId) return;

    setLoadingResults(true);
    try {
      const params = new URLSearchParams({
        scenathonId: String(scenathonId),
        pathwayName: String(pathwayId),
      });

      const res = await apiFetch(
        `${API}/api/scenathon/results?${params.toString()}`,
      );

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || `Scenathon results HTTP ${res.status}`);
      }

      const data = await res.json();
      setResultsPayload(data);
      setResultsError("");
    } catch (err) {
      console.error(err);
      setResultsError(err.message || "Could not load scenathon results.");
    } finally {
      setLoadingResults(false);
    }
  };

  // Cargar pathways disponibles para dashboard cuando cambia el scenathon
  useEffect(() => {
    if (!resultsScenathonId) {
      setDashboardPathways([]);
      setResultsPathwayId("");
      return;
    }

    const loadDashboardPathways = async () => {
      try {
        const res = await apiFetch(
          `${API}/api/scenathon/available-pathways/${resultsScenathonId}`,
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        const nextPathways = data.pathways || [];
        setDashboardPathways(nextPathways);
        setResultsPathwayId((prev) => (
          nextPathways.some((pathway) => String(pathway.value) === String(prev)) ? prev : ""
        ));
      } catch (err) {
        console.error("Error loading dashboard pathways:", err);
        setDashboardPathways([]);
      }
    };

    loadDashboardPathways();
  }, [resultsScenathonId]);

  // 🌍 Cargar países de trade_adjustment_submissions
  useEffect(() => {
    if (!resultsScenathonId || !resultsPathwayId) {
      setCountriesFromSubmissions([]);
      return;
    }

    const loadCountries = async () => {
      setLoadingCountries(true);
      try {
        const params = new URLSearchParams({
          scenathonId: String(resultsScenathonId),
          pathwayName: String(resultsPathwayId),
        });

        const res = await apiFetch(
          `${API}/api/scenathon/trade-adjustment-countries?${params.toString()}`,
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        setCountriesFromSubmissions(Array.isArray(data?.countries) ? data.countries : []);
      } catch (err) {
        console.error("Error loading countries:", err);
        setCountriesFromSubmissions([]);
      } finally {
        setLoadingCountries(false);
      }
    };

    loadCountries();
  }, [resultsScenathonId, resultsPathwayId]);

  const normalized = useMemo(
    () => normalizeResultsPayload(resultsPayload),
    [resultsPayload],
  );

  const countriesFromResults = useMemo(
    () => extractCountriesFromCharts(normalized?.charts),
    [normalized],
  );

  const pathwayNode = normalized?.node || null;
  const pathwayKey = normalized?.pathwayKey || "";

  const pathwayOptions = useMemo(
    () =>
      pathways.map((p) => ({
        value: String(p.id),
        label: p.name || p.pathway_name || String(p.id),
      })),
    [pathways],
  );

  const defaultPathwayOptions = useMemo(
    () =>
      defaultPathways.map((p) => ({
        value: String(p.id),
        label: p.defaultname,
      })),
    [defaultPathways],
  );

  const availablePathwaysInResults = useMemo(() => {
    if (dashboardPathways.length > 0) {
      return dashboardPathways;
    }

    if (!resultsPayload) return [];

    try {
      const root = Array.isArray(resultsPayload) ? resultsPayload[0] : resultsPayload;
      if (!root || typeof root !== "object") return [];

      const keys = Object.keys(root).filter((key) => {
        const excluded = ["id", "error", "status", "message", "timestamp"];
        return !excluded.includes(key.toLowerCase());
      });

      return keys.map((key) => ({
        value: key,
        label: key.replace(/_/g, " ").replace(/scenathon/i, "").trim() || key,
      }));
    } catch (err) {
      console.error("Error extracting available pathways:", err);
      return [];
    }
  }, [dashboardPathways, resultsPayload]);

  const dashboardPathwayOptions =
    availablePathwaysInResults.length > 0
      ? availablePathwaysInResults
      : pathwayOptions;

  const scenathonOptions = useMemo(
    () =>
      scenathons.map((s) => ({
        value: String(s.id),
        label: s.name || s.title || `Scenathon ${s.id}`,
      })),
    [scenathons],
  );

  const selectedDefaultPathwayName = useMemo(() => {
    const found = defaultPathways.find(
      (p) => String(p.id) === String(selectedDefaultPathwayId),
    );
    return found?.defaultname || "";
  }, [defaultPathways, selectedDefaultPathwayId]);

  const activeScenathonId = selectedScenathon?.id || sendScenathonId;
  const isPathwayValid = Boolean(
    selectedPathwayId &&
      pathwayOptions.some((opt) => String(opt.value) === String(selectedPathwayId)),
  );
  const isDefaultPathwayValid = Boolean(
    selectedDefaultPathwayId &&
      defaultPathwayOptions.some(
        (opt) => String(opt.value) === String(selectedDefaultPathwayId),
      ),
  );
  const canSend = Boolean(
    activeScenathonId && isPathwayValid && isDefaultPathwayValid,
  );

  const handleSend = async () => {
    if (!canSend) return;

    setSending(true);
    try {
      const res = await apiFetch(`${API}/api/scenathon/send-pathway`, {
        method: "POST",
        body: JSON.stringify({
          scenathonId: activeScenathonId,
          pathwayId: selectedPathwayId,
          defaultPathwayId: selectedDefaultPathwayId,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || `HTTP ${res.status}`);
      }

      const data = await res.json();

      const finalPathwayName =
        data?.pathwayName || selectedDefaultPathwayName || "";

      setResultsScenathonId(String(activeScenathonId));
      setResultsPathwayId(finalPathwayName);

      await loadScenathonResults(activeScenathonId, finalPathwayName);

      Swal.fire({
        icon: "success",
        title: "Pathway sent successfully",
        text: `The pathway was sent successfully with the name "${finalPathwayName}".`,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Could not send the pathway",
        text: err.message || "An error occurred sending the pathway.",
      });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="rounded-2xl border border-[#E5E7EB] bg-white px-6 py-4 text-sm text-[#6B7280] shadow-sm">
          Loading scenathons and pathways...
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8 w-full">
      <main className="mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6 lg:px-4">
        <div className="mb-8 rounded-xl border border-[#E5E7EB] bg-gradient-to-br from-white to-[#F8FAFC] px-1 py-1 shadow-sm">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="inline-flex w-fit rounded-xl border border-[#E5E7EB] bg-[#F3F4F6] p-1">
              {canSeeSubmission && (
                <button
                  type="button"
                  onClick={() => setSectionTab("submission")}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                    sectionTab === "submission"
                      ? "bg-white text-[#111827] shadow-sm"
                      : "text-[#6B7280] hover:text-[#111827]"
                  }`}
                >
                  Submission
                </button>
              )}

              <button
                type="button"
                onClick={() => setSectionTab("dashboard")}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                  sectionTab === "dashboard"
                    ? "bg-white text-[#111827] shadow-sm"
                    : "text-[#6B7280] hover:text-[#111827]"
                }`}
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>

        {canSeeSubmission && sectionTab === "submission" && (
          <div className="rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
            <div className="grid gap-6 lg:grid-cols-4 lg:items-start">
              <div className="lg:col-span-3">
                <h2 className="text-2xl font-semibold text-[#111827]">
                  Send pathway to Scenathon
                </h2>
                <p className="mt-2 text-sm text-[#6B7280]">
                  Select the scenathon, pathway and default pathway name you want to send.
                </p>
              </div>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {!selectedScenathon?.id && (
                <SelectField
                  label="Scenathon"
                  value={sendScenathonId}
                  onChange={setSendScenathonId}
                  options={scenathonOptions}
                  placeholder="Select scenathon"
                />
              )}

              <SelectField
                label="Pathway"
                value={selectedPathwayId}
                onChange={setSelectedPathwayId}
                options={pathwayOptions}
                placeholder="Select pathway"
              />

              <SelectField
                label="Default pathway name"
                value={selectedDefaultPathwayId}
                onChange={setSelectedDefaultPathwayId}
                options={defaultPathwayOptions}
                placeholder={
                  sendScenathonId
                    ? loadingDefaultPathways
                      ? "Loading default pathways..."
                      : "Select default pathway"
                    : "Select scenathon first"
                }
                disabled={!sendScenathonId || loadingDefaultPathways}
              />

              <div className="flex items-end">
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!canSend || sending}
                  className="inline-flex w-full items-center justify-center rounded-xl bg-[#2E7D32] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#276C2B] disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {sending ? "Sending..." : "Send pathway"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div
          className={`rounded-3xl border border-slate-200 bg-white p-6 shadow-sm ${
            sectionTab !== "dashboard"
              ? "hidden"
              : "mt-6"
          }`}
        >
          <div className="flex flex-col gap-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-[#111827]">
                  Scenathon Dashboard
                </h2>
                <p className="mt-1 text-sm text-[#6B7280]">
                  Switch between results charts and comparison charts.
                </p>
              </div>

              {dashboardTab === "results" && resultsScenathonId && resultsPathwayId && (
                <button
                  type="button"
                  onClick={() => loadScenathonResults(resultsScenathonId, resultsPathwayId)}
                  disabled={loadingResults}
                  title="Refresh charts"
                  className="mt-1 inline-flex items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] shadow-sm transition hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <svg
                    className={`h-4 w-4 ${loadingResults ? "animate-spin" : ""}`}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {loadingResults ? "Refreshing..." : "Update Charts"}
                </button>
              )}
            </div>

            <div className="inline-flex w-fit rounded-xl border border-[#E5E7EB] bg-[#F3F4F6] p-1">
              <button
                type="button"
                onClick={() => setDashboardTab("results")}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                  dashboardTab === "results"
                    ? "bg-white text-[#111827] shadow-sm"
                    : "text-[#6B7280] hover:text-[#111827]"
                }`}
              >
                Results
              </button>
              <button
                type="button"
                onClick={() => setDashboardTab("comparison")}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                  dashboardTab === "comparison"
                    ? "bg-white text-[#111827] shadow-sm"
                    : "text-[#6B7280] hover:text-[#111827]"
                }`}
              >
                Comparison
              </button>
            </div>

            {dashboardTab === "results" ? (
              <div className="grid gap-4 lg:grid-cols-2 lg:items-end">
                {!selectedScenathon?.id && (
                  <SelectField
                    label="Scenathon"
                    value={resultsScenathonId}
                    onChange={setResultsScenathonId}
                    options={scenathonOptions}
                    placeholder="Select scenathon"
                  />
                )}

                <SelectField
                  label="Pathway"
                  value={resultsPathwayId}
                  onChange={setResultsPathwayId}
                  options={dashboardPathwayOptions}
                  placeholder="Select pathway"
                />
              </div>
            ) : (
              <div className="grid gap-4 lg:grid-cols-2 lg:items-end">
                {!selectedScenathon?.id && (
                  <SelectField
                    label="Scenathon"
                    value={resultsScenathonId}
                    onChange={setResultsScenathonId}
                    options={scenathonOptions}
                    placeholder="Select scenathon"
                  />
                )}
              </div>
            )}
          </div>

          {/* Results tab content */}
          <div className={dashboardTab !== "results" ? "hidden" : ""}>
            {(!resultsScenathonId || !resultsPathwayId) ? (
              <div className="mt-6 rounded-2xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-10 text-center text-sm text-[#6B7280]">
                Select a scenathon and pathway in the results section.
              </div>
            ) : loadingResults ? (
              <div className="mt-6 rounded-2xl border border-[#E5E7EB] bg-[#F8FAFC] p-10 text-center text-sm text-[#6B7280]">
                Loading results...
              </div>
            ) : resultsError ? (
              <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">
                {resultsError}
              </div>
            ) : !pathwayNode ? (
              <div className="mt-6 rounded-2xl border border-[#E5E7EB] bg-[#F8FAFC] p-10 text-center text-sm text-[#6B7280]">
                Select a scenathon and pathway to load the charts.
              </div>
            ) : (
              <div className="mt-6 space-y-6">
                <div className="grid gap-4 md:grid-cols-3">
                  <MetricCard label="Pathway" value={pathwayKey} />
                  <MetricCard
                    label="Countries/Regions"
                    value={
                      countriesFromSubmissions.length > 0
                        ? countriesFromSubmissions.join(", ")
                        : countriesFromResults.length > 0
                          ? countriesFromResults.join(", ")
                          : "—"
                    }
                  />
                  <MetricCard label="Date" value={pathwayNode?.date} />
                </div>
                <div className="rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] p-1">
                  <ScenathonCharts
                    resultsData={resultsPayload}
                    scenathonId={resultsScenathonId}
                    pathwayName={resultsPathwayId}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Comparison tab — always mounted to preserve selections */}
          <div className={dashboardTab !== "comparison" ? "hidden" : "mt-6 rounded-2xl border border-[#E5E7EB] bg-[#F8FAFC] p-4"}>
            <Comparison
              scenathonId={resultsScenathonId}
              requireScenathon
              embedded
            />
          </div>
        </div>
      </main>
    </div>
  );
}